import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { ROUTES_CONFIG } from "src/constants/routesConfig";

interface ModalProps {
  onClose?: () => void;
}

interface ErrorProps extends ModalProps {
  onRetry: () => void;
}

interface SuccessModalProps extends ModalProps {
  transferOrderId: string;
  productsCount: number;
}

const Success: React.FC<SuccessModalProps> = ({ transferOrderId, productsCount, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-hidden">
        <div className="overflow-y-auto max-h-[85vh]">
          <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="w-6 h-6 text-success-600" />
              <h2 className="text-lg font-semibold text-gray-900">Transfer Order Created</h2>
            </div>
            {onClose && (
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
                &times;
              </button>
            )}
          </div>

          <div className="p-5 space-y-4">
            <div className="text-center pb-2">
              <p className="text-sm text-gray-700">The transfer order has been created successfully</p>
            </div>

            <div className="bg-success-50 rounded-lg p-4 border border-success-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-600 mb-1">TO ID</p>
                  <p className="text-lg font-bold text-gray-900">{transferOrderId}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Products</p>
                  <p className="text-lg font-bold text-gray-900">{productsCount}</p>
                </div>
              </div>
            </div>

            <div className="text-center border-t border-gray-200 pt-4">
              <p className="text-sm text-gray-600">
                View all transfer orders{" "}
                <Link
                  to={ROUTES_CONFIG.transferOrdersDashboard.path}
                  className="text-black font-semibold hover:text-gray-700 underline"
                >
                  here
                </Link>
              </p>
            </div>

            <div className="flex justify-center gap-3">
              {onClose && (
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium"
                >
                  Create Another
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Error: React.FC<ErrorProps> = ({ onRetry, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">Creation Failed</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5">
          <p className="text-sm text-gray-700 text-center mb-6">
            An error occurred while creating the transfer order. Please try again.
          </p>

          <div className="flex justify-center gap-3">
            <button
              className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
              onClick={onRetry}
            >
              Retry
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateTransferOrderModals = {
  Success,
  Error
};

export { CreateTransferOrderModals };
